/**
 * Custom hook to sync draft data with Redux store
 * Consolidates all Redux dispatch operations for draft loading/saving
 */

import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  setBusinessDoc,
  setCertificateOfIncorporationDoc,
  setProofOfBankAccountDetailsDoc,
  setSupportingDocumentsWithSuppliersDoc,
} from "../../../features/merchant/doc";
import { hydrateDisBizState } from "../../../features/distributor/biz";
import { hydrateDisBankState } from "../../../features/distributor/bank";

export interface DraftData {
  businessProfile?: any[];
  certificateOfIncorporation?: any[];
  proofOfBankAccountDetails?: any[];
  supportingDocumentsWithSuppliers?: any[];
  companyName?: string;
  companyRegistrationNo?: string;
  legalStructure?: string;
  legalStructureOthers?: string;
  legalEntityAddress?: string;
  industryGroup?: string;
  bankName?: string;
  accountType?: string;
  accountNumber?: string;
  accountHolderName?: string;
  swiftCode?: string;
}

/**
 * Hook to sync draft data with Redux store
 */
export const useDraftSync = () => {
  const dispatch = useDispatch();

  /**
   * Sync all draft data to Redux store
   * Uses hydrate pattern for efficient bulk updates
   */
  const syncDraftToRedux = useCallback(
    (data: DraftData) => {
      // Sync document uploads (4 documents) - Use unwrapped payloads
      dispatch(setBusinessDoc(data.businessProfile || []));
      dispatch(
        setCertificateOfIncorporationDoc(data.certificateOfIncorporation || [])
      );
      dispatch(
        setProofOfBankAccountDetailsDoc(data.proofOfBankAccountDetails || [])
      );
      dispatch(
        setSupportingDocumentsWithSuppliersDoc(
          data.supportingDocumentsWithSuppliers || []
        )
      );

      // Sync business info - Single dispatch with hydrateDisBizState
      dispatch(
        hydrateDisBizState({
          company: data.companyName ?? null,
          regNumber: data.companyRegistrationNo ?? null,
          legalStructure: data.legalStructure ?? null,
          legalStructureOthers: data.legalStructureOthers ?? null,
          address: data.legalEntityAddress ?? null,
          industryGroup: data.industryGroup ?? null,
        })
      );

      // Sync bank info - Single dispatch with hydrateDisBankState
      dispatch(
        hydrateDisBankState({
          bankName: data.bankName ?? null,
          accountType: data.accountType ?? null,
          accountNumber: data.accountNumber ?? null,
          accountHolderName: data.accountHolderName ?? null,
          swiftCode: data.swiftCode ?? null,
        })
      );
    },
    [dispatch]
  );

  return { syncDraftToRedux };
};
